import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Paperclip, Upload, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money, norm, fecha } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export const empleadoVacio = {
  nombre: "", cedula: "", usuario_id: "", cargo: "Docente", departamento: "",
  tipo_contrato: "fijo", salario_mensual: "", salario_por_hora: "", fecha_ingreso: "",
  hora_entrada: "", hora_salida: "", tolerancia_minutos: 10,
  banco: "", cuenta_banco: "", afp: "", ars: "", email: "", telefono: "",
  exento_isr: false, activo: true, notas: "",
};

const TIPOS_DOC = [
  ["contrato", "Contrato"], ["cedula", "Cédula"], ["titulo", "Título"],
  ["certificado_medico", "Certificado médico"], ["permiso", "Permiso o licencia"],
  ["evaluacion", "Evaluación"], ["otro", "Otro"],
];

const numero = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

export const EmpleadosPanel = ({ empleados, candidatos, onListo }) => {
  const [form, setForm] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [expediente, setExpediente] = useState(null);
  const [cuentas, setCuentas] = useState(candidatos);

  useEffect(() => { setCuentas(candidatos); }, [candidatos]);

  const abrirEdicion = async (e) => {
    setForm({
      ...empleadoVacio, ...e, usuario_id: e.usuario_id || "",
      salario_mensual: e.salario_mensual ?? "", salario_por_hora: e.salario_por_hora ?? "",
    });
    const { data } = await api.get("/nomina/candidatos", { params: { empleado_id: e.id } });
    setCuentas(data);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Indique el nombre del empleado");
    const payload = {
      ...form,
      usuario_id: form.usuario_id || null,
      hora_entrada: form.hora_entrada || null,
      hora_salida: form.hora_salida || null,
      tolerancia_minutos: Number(form.tolerancia_minutos) || 0,
      salario_mensual: numero(form.salario_mensual),
      salario_por_hora: numero(form.salario_por_hora),
    };
    try {
      if (form.id) await api.put(`/nomina/empleados/${form.id}`, payload);
      else await api.post("/nomina/empleados", payload);
      toast.success(form.id ? "Ficha del empleado actualizada" : "Empleado registrado");
      setForm(null);
      onListo();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (emp) => {
    try {
      const { data } = await api.delete(`/nomina/empleados/${emp.id}`);
      toast.success(data.desactivado ? data.detalle : "Empleado eliminado");
      onListo();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const q = norm(busqueda.trim());
  const visibles = empleados.filter((e) => !q
    || [e.nombre, e.cedula, e.cargo, e.departamento].filter(Boolean).some((v) => norm(v).includes(q)));

  return (
    <div data-testid="panel-empleados">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="rounded-full pl-10" placeholder="Buscar por nombre, cédula o cargo…"
                 value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                 data-testid="buscar-empleado" />
        </div>
        <Button className="rounded-full" data-testid="nuevo-empleado"
                onClick={() => setForm({ ...empleadoVacio })}>
          <Plus className="mr-2 h-4 w-4" /> Nuevo empleado
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="tabla-empleados">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-5 py-3">Empleado</th>
                <th className="px-5 py-3">Cargo</th>
                <th className="px-5 py-3">Contrato</th>
                <th className="px-5 py-3">Salario</th>
                <th className="px-5 py-3">Expediente</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((e) => (
                <tr key={e.id} className="border-t border-border/60 hover:bg-muted/30">
                  <td className="px-5 py-3">
                    <span className="block font-medium">{e.nombre}</span>
                    <span className="block font-mono text-xs text-muted-foreground">
                      {e.cedula || "sin cédula"}{e.activo === false ? " · inactivo" : ""}
                      {e.usuario_id ? " · poncha en su portal" : " · poncha en recepción"}
                    </span>
                  </td>
                  <td className="px-5 py-3">{e.cargo}{e.departamento ? ` · ${e.departamento}` : ""}</td>
                  <td className="px-5 py-3 capitalize">{(e.tipo_contrato || "").replace("_", " ")}</td>
                  <td className="px-5 py-3 font-mono text-xs">
                    {e.tipo_contrato === "por_horas"
                      ? `${money(e.salario_por_hora)} / hora`
                      : money(e.salario_mensual)}
                    {e.hora_entrada && (
                      <span className="block text-[10px] text-muted-foreground">
                        entra {e.hora_entrada} (+{e.tolerancia_minutos ?? 0} min)
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant={e.documentos ? "secondary" : "outline"}>
                      {e.documentos || 0} archivo(s)
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button size="sm" variant="outline" className="rounded-full"
                              onClick={() => setExpediente(e)} data-testid={`expediente-${e.id}`}>
                        <Paperclip className="mr-1 h-3 w-3" /> Expediente
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full"
                              onClick={() => abrirEdicion(e)} data-testid={`editar-empleado-${e.id}`}>
                        <Pencil className="mr-1 h-3 w-3" /> Editar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => eliminar(e)}
                              data-testid={`eliminar-empleado-${e.id}`}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visibles.length === 0 && (
            <p className="p-10 text-center text-sm text-muted-foreground" data-testid="empleados-vacios">
              Todavía no hay empleados registrados.
            </p>
          )}
        </div>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="modal-empleado">
          <DialogHeader>
            <DialogTitle className="font-display">
              {form?.id ? "Editar ficha del empleado" : "Nuevo empleado"}
            </DialogTitle>
            <DialogDescription>
              Los datos de TSS (AFP y ARS) y el salario alimentan el cálculo automático de la nómina.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid gap-4 sm:grid-cols-2">
              {!form.id && cuentas.length > 0 && (
                <div className="space-y-2 sm:col-span-2">
                  <Label>Vincular con una cuenta del personal (para que ponche él mismo)</Label>
                  <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                          value={form.usuario_id} data-testid="empleado-cuenta"
                          onChange={(ev) => {
                            const c = cuentas.find((x) => x.id === ev.target.value);
                            setForm({
                              ...form, usuario_id: ev.target.value,
                              nombre: c?.nombre || form.nombre, email: c?.email || form.email,
                              telefono: c?.telefono || form.telefono,
                              cedula: c?.cedula || form.cedula,
                              cargo: c ? c.rol.charAt(0).toUpperCase() + c.rol.slice(1) : form.cargo,
                            });
                          }}>
                    <option value="">Sin cuenta (poncha en recepción)</option>
                    {cuentas.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre} — {c.rol}</option>
                    ))}
                  </select>
                </div>
              )}
              {form.id && (
                <div className="space-y-2 sm:col-span-2">
                  <Label>Cuenta vinculada (le habilita el ponchador en su portal)</Label>
                  <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                          value={form.usuario_id} data-testid="empleado-cuenta-editar"
                          onChange={(ev) => setForm({ ...form, usuario_id: ev.target.value })}>
                    <option value="">Sin cuenta (poncha en recepción)</option>
                    {cuentas.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre} — {c.rol}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Nombre completo</Label>
                <Input value={form.nombre} data-testid="empleado-nombre"
                       onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Cédula</Label>
                <Input value={form.cedula || ""} className="font-mono" data-testid="empleado-cedula"
                       onChange={(e) => setForm({ ...form, cedula: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Cargo</Label>
                <Input value={form.cargo} data-testid="empleado-cargo"
                       onChange={(e) => setForm({ ...form, cargo: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Departamento</Label>
                <Input value={form.departamento || ""} data-testid="empleado-departamento"
                       onChange={(e) => setForm({ ...form, departamento: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tipo de contrato</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={form.tipo_contrato} data-testid="empleado-tipo-contrato"
                        onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })}>
                  <option value="fijo">Fijo</option>
                  <option value="temporal">Temporal</option>
                  <option value="por_horas">Por horas (según ponches)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>{form.tipo_contrato === "por_horas" ? "Pago por hora (RD$)" : "Salario mensual (RD$)"}</Label>
                <Input type="number" data-testid="empleado-salario"
                       value={form.tipo_contrato === "por_horas" ? form.salario_por_hora : form.salario_mensual}
                       onChange={(e) => setForm(form.tipo_contrato === "por_horas"
                         ? { ...form, salario_por_hora: e.target.value }
                         : { ...form, salario_mensual: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Fecha de ingreso</Label>
                <Input type="date" value={(form.fecha_ingreso || "").slice(0, 10)}
                       data-testid="empleado-ingreso"
                       onChange={(e) => setForm({ ...form, fecha_ingreso: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Hora de entrada pactada</Label>
                <Input type="time" value={form.hora_entrada || ""} data-testid="empleado-hora-entrada"
                       onChange={(e) => setForm({ ...form, hora_entrada: e.target.value })} />
                <p className="text-xs text-muted-foreground">
                  Si poncha después de esta hora más la tolerancia, la marca sale en rojo.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Hora de salida</Label>
                <Input type="time" value={form.hora_salida || ""} data-testid="empleado-hora-salida"
                       onChange={(e) => setForm({ ...form, hora_salida: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tolerancia (minutos)</Label>
                <Input type="number" value={form.tolerancia_minutos ?? 10}
                       data-testid="empleado-tolerancia"
                       onChange={(e) => setForm({ ...form, tolerancia_minutos: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>AFP</Label>
                <Input value={form.afp || ""} data-testid="empleado-afp"
                       onChange={(e) => setForm({ ...form, afp: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>ARS (seguro de salud)</Label>
                <Input value={form.ars || ""} data-testid="empleado-ars"
                       onChange={(e) => setForm({ ...form, ars: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Banco</Label>
                <Input value={form.banco || ""} data-testid="empleado-banco"
                       onChange={(e) => setForm({ ...form, banco: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Cuenta bancaria</Label>
                <Input value={form.cuenta_banco || ""} className="font-mono" data-testid="empleado-cuenta-banco"
                       onChange={(e) => setForm({ ...form, cuenta_banco: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Correo</Label>
                <Input value={form.email || ""} data-testid="empleado-email"
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={form.telefono || ""} data-testid="empleado-telefono"
                       onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              </div>
              <label className="flex items-center gap-3 text-sm sm:col-span-2">
                <Checkbox checked={form.exento_isr} data-testid="empleado-exento-isr"
                          onCheckedChange={(v) => setForm({ ...form, exento_isr: !!v })} />
                Exento de retención del ISR
              </label>
              <label className="flex items-center gap-3 text-sm sm:col-span-2">
                <Checkbox checked={form.activo !== false} data-testid="empleado-activo"
                          onCheckedChange={(v) => setForm({ ...form, activo: !!v })} />
                Empleado activo (entra en la nómina)
              </label>
              <Button className="rounded-full py-6 sm:col-span-2" onClick={guardar}
                      data-testid="guardar-empleado">
                {form.id ? "Guardar cambios" : "Registrar empleado"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ExpedienteEmpleado empleado={expediente} onClose={() => setExpediente(null)} onListo={onListo} />
    </div>
  );
};

const ExpedienteEmpleado = ({ empleado, onClose, onListo }) => {
  const [docs, setDocs] = useState([]);
  const [subida, setSubida] = useState({ tipo: "contrato", nombre: "", archivo_base64: "", nota: "" });

  useEffect(() => {
    if (!empleado) return;
    api.get(`/nomina/empleados/${empleado.id}/documentos`).then((r) => setDocs(r.data));
    setSubida({ tipo: "contrato", nombre: "", archivo_base64: "", nota: "" });
  }, [empleado]);

  const tomarArchivo = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSubida((s) => ({
      ...s, archivo_base64: String(reader.result), nombre: s.nombre || file.name,
    }));
    reader.readAsDataURL(file);
  };

  const subir = async () => {
    if (!subida.archivo_base64) return toast.error("Elija el archivo a subir");
    try {
      await api.post(`/nomina/empleados/${empleado.id}/documentos`, subida);
      toast.success("Documento agregado al expediente");
      const { data } = await api.get(`/nomina/empleados/${empleado.id}/documentos`);
      setDocs(data);
      setSubida({ tipo: "contrato", nombre: "", archivo_base64: "", nota: "" });
      onListo();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const borrar = async (d) => {
    await api.delete(`/nomina/documentos/${d.id}`);
    setDocs(docs.filter((x) => x.id !== d.id));
    onListo();
  };

  const abrir = async (d) => {
    const { data } = await api.get(`/nomina/documentos/${d.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 8000);
  };

  return (
    <Dialog open={!!empleado} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="modal-expediente">
        <DialogHeader>
          <DialogTitle className="font-display">Expediente de {empleado?.nombre}</DialogTitle>
          <DialogDescription>
            Contratos, cédula, títulos, certificados médicos y permisos del empleado.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 rounded-md border border-border bg-muted/30 p-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo de documento</Label>
              <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      value={subida.tipo} data-testid="documento-tipo"
                      onChange={(e) => setSubida({ ...subida, tipo: e.target.value })}>
                {TIPOS_DOC.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={subida.nombre} data-testid="documento-nombre"
                     onChange={(e) => setSubida({ ...subida, nombre: e.target.value })} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Archivo (PDF o imagen)</Label>
              <Input type="file" onChange={tomarArchivo} data-testid="documento-archivo" />
            </div>
            <Button className="rounded-full sm:col-span-2" onClick={subir} data-testid="subir-documento">
              <Upload className="mr-2 h-4 w-4" /> Agregar al expediente
            </Button>
          </div>

          <div className="space-y-2" data-testid="lista-documentos">
            {docs.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-md border border-border/60 p-3 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{d.nombre}</span>
                  <span className="block text-xs text-muted-foreground">
                    {(TIPOS_DOC.find(([v]) => v === d.tipo) || [])[1] || d.tipo} · {fecha(d.creado_en)}
                    {d.subido_por ? ` · ${d.subido_por}` : ""}
                  </span>
                </span>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => abrir(d)}
                        data-testid={`ver-documento-${d.id}`}>
                  <Download className="mr-1 h-3 w-3" /> Ver
                </Button>
                <Button size="sm" variant="ghost" onClick={() => borrar(d)}
                        data-testid={`borrar-documento-${d.id}`}>
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            ))}
            {docs.length === 0 && <Empty testId="documentos-vacios">Expediente vacío.</Empty>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
